/**
 * Organizer filter panel for matching columns.
 * context='session' → "tutors" + "volunteers" role groups.
 * context='event' → same plus the exact coordinator role (not coordinator_remote).
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { Select } from '@/components/forms/Select';
import {
  DAY_KEY_TO_AVAILABILITY_API,
  INPERSON_EXIT_REASON_OPTIONS,
  STAFF_STATUS_CONFIG,
  WEEKDAY_FILTER_OPTIONS,
} from '@/constants/shared-catalog';
import { useRoles } from '@/hooks/useRoles';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import { getSubjects } from '@/services/subjects.service';

/** French labels for organizer matching-column filter ids. */
const ORGANIZER_FILTER_LABELS = {
  'filter-status': 'Statut',
  'filter-role': 'Rôle',
  'filter-subject': 'Matières',
  'filter-establishment': 'Établissement',
  'filter-availability': 'Disponibilité',
  'filter-entry-date': "Date d'entrée",
  'filter-contract-end-date': 'Date de fin engagement',
  'filter-exit-date': 'Date de désactivation',
  'filter-exit-reason': 'Motif de sortie',
} as const;

/** Matching-column context: session organizers vs event organizers (+ coordinator). */
export type OrganizerFilterContext = 'session' | 'event';

/** Filter state for the organizer matching column. */
export interface OrganizerFilters {
  statuses: string[];
  establishmentId?: string;
  roleCodes: string[];
  availability: string[];
  subjects: string[];
  engagementStartDateFrom?: string;
  engagementStartDateTo?: string;
  engagementEndDateFrom?: string;
  engagementEndDateTo?: string;
  exitDateFrom?: string;
  exitDateTo?: string;
  exitReason?: string;
}

/** Empty organizer filters (no status / role / date constraints). */
export const DEFAULT_ORGANIZER_FILTERS: OrganizerFilters = {
  statuses: [],
  establishmentId: undefined,
  roleCodes: [],
  availability: [],
  subjects: [],
  engagementStartDateFrom: undefined,
  engagementStartDateTo: undefined,
  engagementEndDateFrom: undefined,
  engagementEndDateTo: undefined,
  exitDateFrom: undefined,
  exitDateTo: undefined,
  exitReason: undefined,
};

/** Role groups always eligible as session organizers. */
export const ORGANIZER_SESSION_GROUPS = new Set(['tutors', 'volunteers']);

/** In-person tutors/volunteers; event context also includes the exact `coordinator` role. */
function isOrganizerRoleEligible(
  roleGroup: string | undefined,
  code: string,
  mode: string | null | undefined,
  context: OrganizerFilterContext,
): boolean {
  if (!roleGroup) return false;
  if (mode === 'remote') return false;
  if (ORGANIZER_SESSION_GROUPS.has(roleGroup)) return true;
  if (context === 'event' && roleGroup === 'admins' && code === 'coordinator') return true;
  return false;
}

/** Props for {@link OrganizerFiltersContent}. */
export interface OrganizerFiltersContentProps {
  filters: OrganizerFilters;
  context?: OrganizerFilterContext;
  initialEstablishmentOption?: { value: string; label: string };
  visibleFilterIds?: string[];
  sessionAvailabilityDay?: number;
  onToggleStatus: (status: string) => void;
  onSetEstablishment: (id: string | undefined) => void;
  onToggleRoleCode: (code: string) => void;
  onToggleAvailability: (day: string) => void;
  onToggleSubject: (subjectId: string) => void;
  onSetEngagementStartDateRange?: (from: string | undefined, to: string | undefined) => void;
  onSetEngagementEndDateRange?: (from: string | undefined, to: string | undefined) => void;
  onSetExitDateRange?: (from: string | undefined, to: string | undefined) => void;
  onSetExitReason?: (reason: string | undefined) => void;
}

/** Filter controls for organizer-scoped matching columns. */
export const OrganizerFiltersContent: React.FC<OrganizerFiltersContentProps> = ({
  filters,
  context = 'event',
  initialEstablishmentOption,
  visibleFilterIds,
  sessionAvailabilityDay,
  onToggleStatus,
  onSetEstablishment,
  onToggleRoleCode,
  onToggleAvailability,
  onToggleSubject,
  onSetEngagementStartDateRange,
  onSetEngagementEndDateRange,
  onSetExitDateRange,
  onSetExitReason,
}) => {
  const { data: allRoles = [] } = useRoles();

  const { data: subjects = [] } = useQuery({
    queryKey: ['subjects', 'organizer-filters'],
    queryFn: async () => {
      const response = await getSubjects({ page: 1, pageSize: 20 });
      return response.data;
    },
  });

  /** Roles shown in the role chips (context + in-person only). */
  const eligibleRoles = React.useMemo(
    () => allRoles.filter((r) => isOrganizerRoleEligible(r.roleGroup, r.code, r.mode, context)),
    [allRoles, context],
  );

  /** When `visibleFilterIds` is unset, every filter group is shown. */
  const show = React.useCallback(
    (filterId: string) => !visibleFilterIds || visibleFilterIds.includes(filterId),
    [visibleFilterIds],
  );

  /** Normalize availability (array or legacy single string) for chip matching. */
  const selectedDays = React.useMemo(() => {
    const raw = filters.availability;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') return [raw];
    return [];
  }, [filters.availability]);

  const { options, isLoading: estLoading, onSearch } = useEstablishmentsAutocomplete();

  /** Keep the currently selected establishment visible even if it is not in the latest search page. */
  const establishmentOptions = React.useMemo(() => {
    if (
      !initialEstablishmentOption ||
      options.some((o) => o.value === initialEstablishmentOption.value)
    ) {
      return options;
    }
    return [initialEstablishmentOption, ...options];
  }, [options, initialEstablishmentOption]);

  return (
    <div className="filter-groups-container">
      {show('filter-status') && (
        <div className="filter-group">
          <span className="filter-group-label">{ORGANIZER_FILTER_LABELS['filter-status']}</span>
          <div className="filter-options">
            {Object.entries(STAFF_STATUS_CONFIG).map(([key, config]) => {
              const Icon = config.icon;
              const isActive = filters.statuses.includes(key);
              return (
                <Button
                  key={key}
                  variant="custom"
                  size="sm"
                  icon={Icon}
                  iconPosition="left"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                  )}
                  style={
                    {
                      '--filter-color': config.badgeColor ?? 'rgb(var(--status-success))',
                    } as React.CSSProperties
                  }
                  onClick={() => onToggleStatus(key)}
                >
                  {config.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-subject') && subjects.length > 0 && (
        <div className="filter-group">
          <span className="filter-group-label">{ORGANIZER_FILTER_LABELS['filter-subject']}</span>
          <div className="filter-options max-h-48 overflow-y-auto pr-1">
            {subjects.map((subject) => {
              const isActive = filters.subjects.includes(subject.id);
              return (
                <Button
                  key={subject.id}
                  variant="custom"
                  size="sm"
                  icon={BookOpen}
                  iconPosition="left"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                    isActive ? 'filter-option-primary--active' : 'filter-option-primary',
                  )}
                  onClick={() => onToggleSubject(subject.id)}
                >
                  {subject.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-establishment') && (
        <div className="filter-group">
          <span className="filter-group-label">
            {ORGANIZER_FILTER_LABELS['filter-establishment']}
          </span>
          <Autocomplete
            options={establishmentOptions}
            value={filters.establishmentId ?? ''}
            onChange={(val) => onSetEstablishment(val || undefined)}
            onSearch={onSearch}
            isLoading={estLoading}
            placeholder="Filtrer par établissement..."
            minSearchLength={2}
            inputSize="sm"
          />
        </div>
      )}

      {show('filter-role') && eligibleRoles.length > 0 && (
        <div className="filter-group">
          <span className="filter-group-label">{ORGANIZER_FILTER_LABELS['filter-role']}</span>
          <div className="filter-options flex-wrap">
            {eligibleRoles.map((role) => {
              const isActive = filters.roleCodes.includes(role.code);
              return (
                <Button
                  key={role.code}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                  )}
                  style={{ '--filter-color': 'var(--color-2-nuance-1)' } as React.CSSProperties}
                  onClick={() => onToggleRoleCode(role.code)}
                >
                  {role.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-availability') && (
        <div className="filter-group">
          <span className="filter-group-label">
            {ORGANIZER_FILTER_LABELS['filter-availability']}
          </span>
          <div className="filter-options">
            {WEEKDAY_FILTER_OPTIONS.map((day) => {
              const isActive = selectedDays.includes(day.value);
              const isSessionDay =
                sessionAvailabilityDay === DAY_KEY_TO_AVAILABILITY_API[day.value];
              const isDisabled = isSessionDay;
              return (
                <Button
                  key={day.value}
                  variant="custom"
                  size="sm"
                  icon={Calendar}
                  iconPosition="left"
                  disabled={isDisabled}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                    isActive ? 'filter-option-secondary--active' : 'filter-option-secondary',
                    isDisabled && 'opacity-70 cursor-not-allowed',
                  )}
                  onClick={() => !isDisabled && onToggleAvailability(day.value)}
                >
                  {day.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-entry-date') && onSetEngagementStartDateRange && (
        <div className="filter-group">
          <span className="filter-group-label">{ORGANIZER_FILTER_LABELS['filter-entry-date']}</span>
          <div className="w-auto">
            <DateRangeInput
              startDate={filters.engagementStartDateFrom}
              endDate={filters.engagementStartDateTo}
              onStartDateChange={(val) =>
                onSetEngagementStartDateRange(val || undefined, filters.engagementStartDateTo)
              }
              onEndDateChange={(val) =>
                onSetEngagementStartDateRange(filters.engagementStartDateFrom, val || undefined)
              }
              layout="horizontal"
              showConnector={true}
              showPresets={false}
              hidePickerLabels={true}
              compact={true}
            />
          </div>
        </div>
      )}

      {show('filter-contract-end-date') && onSetEngagementEndDateRange && (
        <div className="filter-group">
          <span className="filter-group-label">
            {ORGANIZER_FILTER_LABELS['filter-contract-end-date']}
          </span>
          <div className="w-auto">
            <DateRangeInput
              startDate={filters.engagementEndDateFrom}
              endDate={filters.engagementEndDateTo}
              onStartDateChange={(val) =>
                onSetEngagementEndDateRange(val || undefined, filters.engagementEndDateTo)
              }
              onEndDateChange={(val) =>
                onSetEngagementEndDateRange(filters.engagementEndDateFrom, val || undefined)
              }
              layout="horizontal"
              showConnector={true}
              showPresets={false}
              hidePickerLabels={true}
              compact={true}
            />
          </div>
        </div>
      )}

      {show('filter-exit-date') && onSetExitDateRange && (
        <div className="filter-group">
          <span className="filter-group-label">{ORGANIZER_FILTER_LABELS['filter-exit-date']}</span>
          <div className="w-auto">
            <DateRangeInput
              startDate={filters.exitDateFrom}
              endDate={filters.exitDateTo}
              onStartDateChange={(val) => onSetExitDateRange(val || undefined, filters.exitDateTo)}
              onEndDateChange={(val) => onSetExitDateRange(filters.exitDateFrom, val || undefined)}
              layout="horizontal"
              showConnector={true}
              showPresets={false}
              hidePickerLabels={true}
              compact={true}
            />
          </div>
        </div>
      )}

      {show('filter-exit-reason') && onSetExitReason && (
        <div className="filter-group">
          <span className="filter-group-label">
            {ORGANIZER_FILTER_LABELS['filter-exit-reason']}
          </span>
          <div className="w-64">
            <Select
              key={filters.exitReason ?? '__empty__'}
              options={[...INPERSON_EXIT_REASON_OPTIONS]}
              value={filters.exitReason ?? ''}
              onChange={(val) => onSetExitReason(val || undefined)}
              placeholder="Tous les motifs"
              size="sm"
            />
          </div>
        </div>
      )}
    </div>
  );
};
